// covered-by: frontends/carrie/src/routes/ask-puntt/utils/__tests__/visibleComments.test.ts
// covered-by: frontends/carrie/src/utils/__tests__/visibleReviewComments.test.ts
/** Minimum fields for reject/visibility filtering (ticket + AskPunttReview comments). */
export type ReviewCommentVisibilityFields = {
  isDeleted?: boolean;
  liked?: boolean | null;
  isAI?: boolean;
  /** Set when a reviewer already rejected this finding, here or elsewhere. */
  previouslyRejected?: { judgmentId: string } | null;
  /** Set when a reviewer already confirmed this finding, here or elsewhere. */
  previouslyConfirmed?: { judgmentId: string } | null;
  /** Set when she restored, in this review, a finding a past review rejected. */
  restoredAt?: string | null;
  /**
   * Set when an earlier review of this artwork raised the same finding, with
   * what was decided on it there.
   */
  earlierComment?: {
    reviewId: string;
    disposition?: "open" | "accepted" | "rejected";
  } | null;
  /** warhol's intent id; `<raiser>-carried-<commentId>` on a carried finding. */
  intentId?: string;
};

/** Soft-deleted or reviewer-rejected comments stay out of review UI lists. */
export function isVisibleReviewComment(
  comment: ReviewCommentVisibilityFields,
): boolean {
  return !comment.isDeleted && comment.liked !== false;
}

export function isRejectedReviewComment(
  comment: ReviewCommentVisibilityFields,
): boolean {
  return !comment.isDeleted && comment.liked === false;
}

export function visibleReviewComments<T extends ReviewCommentVisibilityFields>(
  comments: T[] | undefined,
): T[] {
  return (comments ?? []).filter(isVisibleReviewComment);
}

export function rejectedReviewComments<T extends ReviewCommentVisibilityFields>(
  comments: T[] | undefined,
): T[] {
  return (comments ?? []).filter(isRejectedReviewComment);
}

export function rejectedReviewCommentCount(
  comments: ReviewCommentVisibilityFields[] | undefined,
): number {
  return rejectedReviewComments(comments).length;
}

/**
 * Findings she has already ruled on, sorted below the ones she has not.
 *
 * Demoted, never dropped: a finding she rejected before still appears, so a
 * wrongly learned rejection is visible rather than silently hiding a real
 * defect. Stable within each group, so the order she is used to survives.
 *
 * why here and not only in warhol: warhol sorts the `ReviewOutcome`, and the
 * panel renders christo's comments, which are a different list. Sorting only
 * there put demoted findings back at the top of the one she reads.
 */
export function demotedLast<T extends ReviewCommentVisibilityFields>(
  comments: T[],
): T[] {
  return [...comments].sort((a, b) => {
    const left = a.previouslyRejected ? 1 : 0;
    const right = b.previouslyRejected ? 1 : 0;
    return left - right;
  });
}

/** Comments shown in the review panel carousel (optionally includes rejected). */
export function reviewCommentsForPanel<T extends ReviewCommentVisibilityFields>(
  comments: T[] | undefined,
  includeRejected: boolean,
): T[] {
  const list = comments ?? [];
  if (includeRejected) {
    return demotedLast(list.filter((c) => !c.isDeleted));
  }
  return demotedLast(visibleReviewComments(list));
}

/**
 * Where a finding sits in the Ask v2 findings list, in display order:
 * Unresolved, Approved, Rejected.
 *
 * Approved and Rejected hold a decision made in this review or in a past one;
 * Unresolved holds every finding nobody has decided yet, raised first in this
 * review or carried from a past one. A decision made in this review outranks a
 * past one: it is the newer.
 */
export const REVIEW_COMMENT_GROUPS = [
  "unresolved",
  "confirmed",
  "rejected",
] as const;
export type ReviewCommentGroup = (typeof REVIEW_COMMENT_GROUPS)[number];

export function reviewCommentGroup(
  comment: ReviewCommentVisibilityFields,
): ReviewCommentGroup {
  // why her verdict here first: `liked` is the one judgment christo never
  // overwrites on a poll, so it is the newest word, and approving a finding a
  // past review rejected must file it under Approved.
  // The card's dim does NOT follow this order: `AskV2ReviewCommentsList` keys
  // it off `isRejectedBefore`, so an overturned past reject stays legible.
  if (comment.liked === false) return "rejected";
  if (comment.liked === true) return "confirmed";
  // A restore is her verdict too, newer than the past rejection.
  if (comment.restoredAt != null) return "unresolved";
  return pastReviewCommentGroup(comment);
}

/** The section past reviews' decisions put a finding in, none made here. */
export function pastReviewCommentGroup(
  comment: ReviewCommentVisibilityFields,
): ReviewCommentGroup {
  if (isRejectedBefore(comment)) return "rejected";
  if (isApprovedBefore(comment)) return "confirmed";
  return "unresolved";
}

/**
 * Raised by an earlier review of this artwork. `earlierComment` is what warhol
 * sets on a finding matching a comment of an earlier review -- a comment it
 * carried forward and one it raised afresh alike. A carried finding whose match
 * did not fire has no `earlierComment`, but its intent id still names the
 * earlier comment it carries (spec 0063), so it counts as raised before too.
 */
export function isRaisedBefore(
  comment: ReviewCommentVisibilityFields,
): boolean {
  return comment.earlierComment != null || isCarriedIntentId(comment.intentId);
}

/**
 * Rejected in a past review: the ledger's receipt, or the earlier comment's
 * own disposition when no receipt matched.
 */
export function isRejectedBefore(
  comment: ReviewCommentVisibilityFields,
): boolean {
  return (
    comment.previouslyRejected != null ||
    comment.earlierComment?.disposition === "rejected"
  );
}

/** Approved in a past review, as `isRejectedBefore` reads a rejection. */
export function isApprovedBefore(
  comment: ReviewCommentVisibilityFields,
): boolean {
  return (
    comment.previouslyConfirmed != null ||
    comment.earlierComment?.disposition === "accepted"
  );
}

// why: warhol's `carried_intent_id` writes `<raiser>-carried-<commentId>` with
// a 24-hex Mongo id; matching the whole tail keeps a raiser or rule slug that
// merely contains "carried" from reading as raised before.
const CARRIED_INTENT_ID = /-carried-[0-9a-f]{24}$/;

function isCarriedIntentId(intentId: string | undefined): boolean {
  return intentId != null && CARRIED_INTENT_ID.test(intentId);
}

/**
 * The section each finding sat in when the review loaded, by comment id. It
 * fixes the panel's order, and so its numbers, for the whole visit; the list's
 * sections follow a judgment at once (`reviewCommentGroup`).
 */
export type LoadedReviewCommentGroups = ReadonlyMap<string, ReviewCommentGroup>;

/**
 * `loaded` with every finding it has not seen yet added at the section it is
 * in now -- a finding a running review writes later, or a comment she adds.
 * Returns `loaded` itself when nothing is new, so a memo keyed on it holds.
 */
export function withLoadedReviewCommentGroups(
  loaded: LoadedReviewCommentGroups,
  comments: readonly (ReviewCommentVisibilityFields & { _id: string })[],
): LoadedReviewCommentGroups {
  const added = comments.filter((comment) => !loaded.has(comment._id));
  if (added.length === 0) return loaded;
  const next = new Map(loaded);
  for (const comment of added) {
    next.set(comment._id, reviewCommentGroup(comment));
  }
  return next;
}

/**
 * Comments the Ask v2 review panel draws: every live finding, the ones
 * rejected in this review included, in the order of the sections each sat in
 * when the review loaded (unresolved, approved, rejected), and inside
 * Unresolved the findings raised first in this review before the ones a past
 * review raised. With no `loaded`, the comments are a fresh load.
 *
 * why a separate path and not `reviewCommentsForPanel(list, true)` at the
 * call site: v1 still hides a rejection by default behind its toggle, while
 * v2 never hides one -- it draws it grayed, so a reject stays visible and can
 * be undone without first finding a control. Two names keep a later v1 change
 * from quietly hiding v2 findings again.
 *
 * why sorted by section, not left in stored order: the list numbers findings
 * in this order and the artwork pins carry the same numbers, so a section that
 * is not contiguous would number its cards 6, 9, 14 instead of 6, 7, 8.
 * why the loaded section, not the current one: a judgment made here moves the
 * card to its new section in the list, but must not renumber any finding or
 * pin under her hand. Stable within a section.
 */
export function askV2ReviewPanelComments<
  T extends ReviewCommentVisibilityFields & { _id: string },
>(comments: T[] | undefined, loaded?: LoadedReviewCommentGroups): T[] {
  const live = (comments ?? []).filter((c) => !c.isDeleted);
  const groups = withLoadedReviewCommentGroups(loaded ?? new Map(), live);
  // why new before past inside Unresolved: the findings this review raised
  // first are the ones she has not seen; the carried ones follow them.
  const rank = (comment: T) => {
    const group = groups.get(comment._id) ?? reviewCommentGroup(comment);
    const carried = group === "unresolved" && isRaisedBefore(comment) ? 1 : 0;
    return REVIEW_COMMENT_GROUPS.indexOf(group) * 2 + carried;
  };
  return live.sort((a, b) => rank(a) - rank(b));
}

export function visibleAiCommentCount(
  comments: (ReviewCommentVisibilityFields & { isAI?: boolean })[] | undefined,
): number {
  return visibleReviewComments(comments).filter((c) => c.isAI).length;
}

export function visibleHumanCommentCount(
  comments: (ReviewCommentVisibilityFields & { isAI?: boolean })[] | undefined,
): number {
  return visibleReviewComments(comments).filter((c) => !c.isAI).length;
}
